import { useState, useEffect, useMemo, Fragment, memo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { Loader, ViewOnly, ButtonV1, ModalBox, DialogBox } from "components";
import CreateShipment from "./CreateShipment";
import ShipmentUpdate from "./ShipmentUpdate";
import PayoutCreationModal from "../DetailsTab/PayoutCreationModal";

import { useFetchData, useToaster } from "hooks";
import { postCall_v2, getCall_v2 } from "services";
import { setShipmentDetailsAction } from "dataStore/shipmentDetails";
import { enableShipmentCreation } from "./shipmentCalculationUtil";
import { orderStatus, SHORT_CLOSE_MSG } from "./constants";
import {
  getFtStatusHistoryUrl,
  buildFtStatusHistoryMap,
  getFtStatusEvents,
} from "./DataModel/ftStatusHistory";
import { error } from "constants";

const SellerOrderDetail = memo(({ sellerOrderNumber, companyName }) => {
  return (
    <Box style={{ display: "flex" }}>
      <ViewOnly label="Seller Order - " value={sellerOrderNumber} />
      <Typography sx={{ ml: 4, mr: 4 }}>|</Typography>
      <ViewOnly label="Seller Name - " value={companyName} />
    </Box>
  );
});

const ShipmentsTab = () => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPayoutModal, setIsPayoutModal] = useState(false);
  const [isShortCloseLoader, setIsShortCloseLoader] = useState(false);
  const [tcSyncData, setTcSyncData] = useState([]);
  const [ftStatusHistoryMap, setFtStatusHistoryMap] = useState({});

  const { id } = useParams();
  const { search } = useLocation();
  const dispatch = useDispatch();
  const triggerToaster = useToaster();

  const orderDetails = useSelector((state) => state?.orderDetails);
  const { orderStatus: currentOrderStatus } = orderDetails;

  const [openShipmentCreateModal, setOpenShipmentCreateModal] = useState(false);
  const [shipmentData, setShipmentData] = useState([]);

  const shipmentIdParam = new URLSearchParams(search).get("shipmentId");

  const { isLoading, refetch: getShipmentData } = useFetchData(
    "shipment-details",
    `/oms/shipment/order-number/${id}`,
    (res) => {
      const data = res?.data?.sellerShipmentsInfo ?? [];
      setShipmentData(data);
      dispatch(setShipmentDetailsAction(data));
    },
  );

  const { data: zohoProcessingCharges, refetch: fetchProcessingCharges } =
    useFetchData("processing-charges", `/oms/shipment/processing-charges`);

  useEffect(() => {
    orderDetails.orderStatus === orderStatus.placed &&
      triggerToaster("Please confirm order, to create shipment", error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetails]);

  const { refetch: fetchOrderRecords, data: dispatchRecord } = useFetchData(
    "dispatch-order-record",
    `/oms/plant-supply/requested-payments?orderNumber=${orderDetails?.orderNumber}`,
  );
  useEffect(() => {
    fetchOrderRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyElegibilityOfShortClose = async () => {
    const res = await postCall_v2(`/oms/shipment/check-short-close`, {
      customerOrderId: orderDetails?.id,
    });
    if (res?.data?.isSuccess) {
      setIsConfirmModalOpen(res?.data?.ableToShortClose);
    }
  };

  useEffect(() => {
    getShipmentData();
    fetchProcessingCharges();
    void verifyElegibilityOfShortClose();
    /*eslint-disable */
  }, []);

  const handlePayoutConfirmation = async (isPayout) => {
    await handleShortClose(isPayout);
  };

  const handleShortClose = async (isPayout) => {
    try {
      setIsShortCloseLoader(true);
      const res = await postCall_v2(
        `oms/order/short-close`,
        {},
        {
          customerOrdNum: orderDetails?.orderNumber,
          isPayoutApplicable: isPayout,
          isManualShortClose: true,
        },
      );
      if (res) {
        dispatch(updateOrderDetailsAction(orderDetails?.orderNumber));
        triggerToaster(`SKU's short closed`, success);
      }
    } catch (err) {
      triggerToaster("Something went wrong. Try after sometime", error);
    } finally {
      setIsConfirmModalOpen(false);
      setIsPayoutModal(false);
      setIsShortCloseLoader(false);
    }
  };

  const shipmentCreateModal = () =>
    setOpenShipmentCreateModal(!openShipmentCreateModal);

  const fetchNewShipment = () => {
    shipmentCreateModal();
    getShipmentData();
  };

  const calculatePendingQuantity = () => {
    return dispatchRecord?.data?.plantSupplyRequestPayments?.reduce(
      (total, item) => {
        if (item.transactionStatus === "Release") {
          const pendingQty =
            (item.psPlannedQuantity || 0) - (item.psShippedQuantity || 0);
          return total + pendingQty;
        }
        return total;
      },
      0,
    );
  };

  const totalPendingQty = calculatePendingQuantity();

  const isShipmentCreationEnabled = useMemo(
    () => enableShipmentCreation(shipmentData, orderDetails, totalPendingQty),
    [shipmentData, orderDetails],
  );

  useEffect(() => {
    currentOrderStatus === orderStatus.placed &&
      triggerToaster("Please confirm order, to create shipment", error);
  }, [currentOrderStatus]);

  useEffect(() => {
    getShipmentData();
    fetchProcessingCharges();
  }, []);

  const handleConfirm = () => {
    setIsPayoutModal(true);
  };

  const fetchTcSyncStatus = async () => {
    try {
      const res = await getCall_v2(`/joms/api/tc-status/${id}`);
      const { data } = res;
      setTcSyncData(data);
    } catch {
      triggerToaster("Failed to fetch TC sync status", error);
    }
  };

  useEffect(() => {
    fetchTcSyncStatus();
  }, []);

  // One call per order; each shipment picks its events from the map
  const fetchFtStatusHistory = async () => {
    try {
      const res = await getCall_v2(getFtStatusHistoryUrl(id));
      setFtStatusHistoryMap(buildFtStatusHistoryMap(res?.data?.shipments));
    } catch {
      // Non-blocking: tooltip falls back to its empty state
      setFtStatusHistoryMap({});
    }
  };

  useEffect(() => {
    fetchFtStatusHistory();
  }, [id]);

  if (isLoading) return <Loader />;

  return (
    <>
      <Typography variant="h3">Shipment details</Typography>

      {shipmentData &&
        shipmentData.map((element, index) => {
          const lineItems = element?.lineItems;
          return (
            <Fragment key={index}>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 16,
                }}
              >
                <SellerOrderDetail
                  sellerOrderNumber={element.sellerOrderNumber}
                  companyName={element?.sellerInfo?.companyName}
                />
                {!index && (
                  <ButtonV1
                    title="Create shipment"
                    onClick={shipmentCreateModal}
                    color="secondary"
                    disabled={!isShipmentCreationEnabled}
                  />
                )}
              </Box>
              {element?.shipmentDetails?.length &&
                element.shipmentDetails.map((data, _) => {
                  const sellerShipmentsInfo =
                    lineItems?.[0]?.sellerShippingAddress;
                  const shipmentInfoObject = {
                    ...data,
                    sellerShipmentsInfo,
                  };
                  return (
                    <ShipmentUpdate
                      shipmentInfo={shipmentInfoObject}
                      lineItems={lineItems}
                      key={data.id}
                      id={Number(shipmentIdParam)}
                      getShipmentData={getShipmentData}
                      zohoProcessingCharges={zohoProcessingCharges?.data?.data}
                      tcSyncData={tcSyncData}
                      ftStatusEvents={getFtStatusEvents(
                        ftStatusHistoryMap,
                        data.number,
                      )}
                    />
                  );
                })}
              <DialogBox
                handleClose={() => setIsConfirmModalOpen(false)}
                open={isConfirmModalOpen}
                handleConfirm={handleConfirm}
                message={SHORT_CLOSE_MSG}
                title=""
                msgStyle={{
                  color: (theme) => theme.palette.grey["A800"],
                  fontWeight: "600",
                  fontSize: 16,
                }}
                buttonWrapperStyle={{ justifyContent: "center" }}
                okButtonVariant="contained"
                cancelButtonVariant="outlined"
                isCloseIcon={true}
              />

              <PayoutCreationModal
                isShortCloseLoader={isShortCloseLoader}
                isShortClosePayout={true}
                open={isPayoutModal}
                onCloseModal={() => setIsPayoutModal(false)}
                onConfirm={handlePayoutConfirmation}
              />
            </Fragment>
          );
        })}

      <ModalBox
        open={openShipmentCreateModal}
        onCloseModal={shipmentCreateModal}
        width={"60%"}
      >
        <CreateShipment
          shipmentData={shipmentData}
          closeModal={fetchNewShipment}
        />
      </ModalBox>
    </>
  );
};

export default ShipmentsTab;
